"use client"

/**
 * React component representing the Future of Robotics section.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - Data for the component, including information for the Future of Robotics section.
 * @returns {JSX.Element} React component.
 */

import Image from "next/image";
import React, { useCallback, useEffect, useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import styles from "./FutureOfRobotics.module.scss";
import LazyVideoNew from "../LazyVideo/LazyVideoNew";
import { LinkWithArrows } from "../common";
import Link from "next/link";
import threeGuys from "@assets/images/homepage/people_3.webp";
import april from "@assets/images/homepage/april.webp";
import teamPhoto from "@assets/images/homepage/team-photo.webp";
import cofounders from "@assets/images/homepage/cofounders.webp";
import verticalTrack from "@assets/images/homepage/vertical_track.webp";
import horizontalTrack from "@assets/images/homepage/horizontal_track.webp";
import movingRobot from "@assets/images/homepage/matic_moving_top.webp";

const transitionLine = { duration: 6, ease: "easeInOut" };

export default function FutureOfRobotics({ data }) {
  const ref = useRef();
  const robotTrackInView = useInView(ref, { once: true, amount: 0.2 });
  const maticImageRef = useRef();
  const controls = useAnimation();
  const pathControls = useAnimation();

  const mobRef = useRef();
  const mobMaticImageRef = useRef();
  const mobRobotTrackInView = useInView(mobRef, { once: true, amount: 0.2 });
  const mobControls = useAnimation();
  const mobPathControls = useAnimation();

  const hideMaticImage = useCallback(async () => {
    if (maticImageRef?.current) maticImageRef.current.className = "hidden";
  }, [maticImageRef?.current]);

  useEffect(() => {
    if (robotTrackInView) {
      controls.start({ offsetDistance: "97%" });
      pathControls.start({ pathLength: 1 });
    }

    return () => {
      controls.stop();
      pathControls.stop();
    };
  }, [robotTrackInView]);

  useEffect(() => {
    if (mobRobotTrackInView) {
      mobControls.start({ offsetDistance: "100%" });
      mobPathControls.start({ pathLength: 1 });
    }

    return () => {
      mobControls.stop();
      mobPathControls.stop();
    };
  }, [mobRobotTrackInView]);

  return (
    <section className="lg:block mob:pb-0 lg:mt-[160px] mt-[80px]">
      <div className="w-[100%] relative">
        <div className="lg:aspect-w-180 lg:aspect-h-103 aspect-w-375 mob:aspect-h-644 aspect-h-446">
          <div className="h-full w-full">
            <div className="h-full relative">
              <div
                className={`lg:w-[19.44%] w-[22.73%] absolute lg:left-[22.3%] mob:top-[16%] top-[21%] lg:top-0 lg:before:bg-white lg:before:absolute lg:before:w-[80px] lg:before:left-[100%] lg:before:h-[100%]`}
              >
                <div className="lg:aspect-w-1 lg:aspect-h-1 aspect-w-104 aspect-h-107">
                  <Image
                    src={threeGuys}
                    layout="fill"
                    alt="Future Robotics 3 Guys Image"
                    className="custom-img"
                  />
                </div>
              </div>
              <div
                className={`lg:w-[41.11%] w-full px-[24px] lg:px-0 absolute mob:top-[50%] top-[66%] lg:top-[43%] lg:left-[29.5%]`}
              >
                <div className="lg:aspect-w-296 lg:aspect-h-149 aspect-w-109 mob:aspect-h-88 aspect-h-46">
                  <div className="flex flex-col">
                    <h3 className={`text-center`}>
                      {data?.futureOfRoboticsTitle}
                    </h3>
                    <p className={`mt-[16px] text-center`}>
                      {data?.futureOfRoboticsDescription}
                    </p>
                    <Link
                      href="/careers"
                      target="_blank"
                    >
                      <LinkWithArrows
                        text={data?.interestedInMaticWorkWithUsText}
                      />
                    </Link>
                  </div>
                </div>
              </div>
              <div
                className={`lg:w-[22.36%] w-[42.93%] absolute mob:top-[27%] top-[36%] lg:top-[74%] lg:left-[5.6%] left-[7%] -z-[1] lg:z-0 lg:h-[275px] lg:bg-white`}
              >
                <div className="lg:aspect-w-322 lg:aspect-h-151 aspect-w-232 aspect-h-108">
                  <Image
                    src={teamPhoto}
                    layout="fill"
                    alt="Future Robotics Team Image"
                    className="lg:rounded-[16px] rounded-[8px]"
                  />
                </div>
              </div>
              <div
                className={`lg:w-[16.66%] w-[26.4%] absolute lg:right-[15.7%] lg:top-[9.5%] top-0 right-[21.2%]`}
              >
                <div className="aspect-w-1 aspect-h-1">
                  <Image
                    src={cofounders}
                    layout="fill"
                    alt="Future Robotics 2 Guys Image"
                    className="custom-img lg:rounded-[16px] rounded-[8px]"
                  />
                </div>
              </div>
              <div
                className={`lg:w-[14.93%] w-[23.46%] absolute lg:right-[5.6%] lg:top-[35%] right-[4%] top-[7.2%] -z-[1]`}
              >
                <div className="lg:aspect-w-215 lg:aspect-h-322 aspect-w-88 aspect-h-132">
                  <Image
                    src={april}
                    layout="fill"
                    alt="Future Robotics April Image"
                    className="h-full w-full object-cover object-center rounded-[8px]"
                  />
                </div>
              </div>
              <div
                className={`lg:w-[18.61%] w-[58.66%] absolute right-[6%] lg:right-[9%] top-[5%] lg:top-[19%] -z-[2]`}
              >
                <div className="lg:aspect-w-67 lg:aspect-h-146 aspect-w-220 aspect-h-129">
                  <Image
                    src={verticalTrack}
                    layout="fill"
                    alt="Future Robotics Image 3"
                    className="custom-img lg:block hidden"
                  />
                  <Image
                    src={horizontalTrack}
                    layout="fill"
                    alt="Future Robotics Image 3"
                    className="custom-img lg:hidden block"
                  />
                </div>
              </div>
              {/* Desktop SVG Animation */}
              <div
                className={`lg:w-[15.90%] absolute lg:top-[13.75%] lg:left-[9.2%] hidden lg:block`}
                ref={ref}
              >
                <div className="aspect-w-229 aspect-h-544 relative -z-[1]">
                  <svg
                    width="232"
                    height="502"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <motion.path
                      d="M350 3H131C60.3076 3 3 60.3075 3 131V730"
                      stroke="#ECEAE3"
                      strokeWidth="6"
                      strokeLinecap="round"
                      fill="transparent"
                      initial={{ pathLength: 0 }}
                      animate={pathControls}
                      transition={transitionLine}
                    />
                    <motion.path
                      d="M350 36H131C77.9807 36 35 78.9807 35 132V730"
                      stroke="#ECEAE3"
                      strokeWidth="6"
                      fill="transparent"
                      initial={{ pathLength: 0 }}
                      animate={pathControls}
                      transition={transitionLine}
                    />
                    <motion.path
                      d="M350 68L131 68C96.2061 68 68 96.2061 68 131V730"
                      stroke="#ECEAE3"
                      strokeWidth="6"
                      fill="transparent"
                      initial={{ pathLength: 0 }}
                      animate={pathControls}
                      transition={transitionLine}
                    />
                  </svg>
                  <motion.div
                    className={`${styles.maticRobotContainer} w-[182px] h-[136px]`}
                    initial={{ offsetDistance: "0%" }}
                    animate={controls}
                    transition={{
                      duration: 4.5,
                      ease: "easeInOut",
                      delay: 0.45,
                    }}
                    onAnimationComplete={hideMaticImage}
                  >
                    <Image
                      ref={maticImageRef}
                      src={movingRobot}
                      className="w-[182px] h-[136px]"
                      alt="Moving Robot"
                      width="182"
                      height="136"
                    />
                  </motion.div>
                </div>
              </div>
              {/* Mobile SVG Animation */}
              {/* <div
                className={`w-[42.93%] absolute top-[89%] block lg:hidden`}
                ref={mobRef}
              >
                <div className="relative -z-[1]">
                  <svg
                    width="200"
                    height="51"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <motion.path
                      d="M0 2H200"
                      stroke="#ECEAE3"
                      strokeWidth="4"
                      strokeLinecap="round"
                      fill="transparent"
                      initial={{ pathLength: 0 }}
                      animate={mobPathControls}
                      transition={transitionLine}
                    />

                    <motion.path
                      d="M0 25.5H200"
                      stroke="#ECEAE3"
                      strokeWidth="4"
                      fill="transparent"
                      initial={{ pathLength: 0 }}
                      animate={mobPathControls}
                      transition={transitionLine}
                    />
                    <motion.path
                      d="M0 49H200"
                      stroke="#ECEAE3"
                      strokeWidth="4"
                      fill="transparent"
                      initial={{ pathLength: 0 }}
                      animate={mobPathControls}
                      transition={transitionLine}
                    />
                  </svg>
                  <motion.div
                    className={`${styles.maticHorTrackContainer} w-[121px] h-[78px]`}
                    initial={{ offsetDistance: "0%" }}
                    animate={mobControls}
                    transition={transitionLine}
                    onAnimationComplete={hideMobileMaticImage}
                  >
                    <Image
                      ref={mobMaticImageRef}
                      src={`https:${data?.futureOfRoboticsMaticRobotImage?.fields?.file?.url}`}
                      className="w-[121px] h-[78px]"
                      alt="Moving Robot"
                      width="121"
                      height="78"
                    />
                  </motion.div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
